import type {
  BillingClient,
  BusinessInfo,
  ClientInvoice,
  InvoiceLineItem,
  PoGstMode,
  PurchaseOrder,
  Supplier,
  SupplierInvoice,
} from '../types';
import { computePoTotals } from './nzGst';

export interface InvoiceValidationIssue {
  level: 'error' | 'warning';
  message: string;
}

function linesToPoShape(items: InvoiceLineItem[]) {
  return items.map((line) => ({
    lineId: line.lineId,
    productName: line.description,
    quantity: line.quantity,
    costPerUnit: line.unitPrice,
    unit: line.unit as PurchaseOrder['items'][0]['unit'],
  }));
}

export function validateBusinessInfoForPoPrint(
  businessInfo?: BusinessInfo | null,
): InvoiceValidationIssue[] {
  const issues: InvoiceValidationIssue[] = [];
  if (!businessInfo?.businessName?.trim()) {
    issues.push({ level: 'error', message: 'Business name is required in Settings.' });
  }
  if (!businessInfo?.address?.trim()) {
    issues.push({ level: 'warning', message: 'Business address is missing in Settings.' });
  }
  if (!businessInfo?.gstNumber?.trim()) {
    issues.push({ level: 'warning', message: 'GST number is missing in Settings.' });
  }
  return issues;
}

export function validatePoPrint(
  po: PurchaseOrder,
  businessInfo?: BusinessInfo | null,
): InvoiceValidationIssue[] {
  const issues = validateBusinessInfoForPoPrint(businessInfo);
  const gstMode = po.gstMode ?? 'inclusive';
  const { totalIncGst } = computePoTotals(po.items, gstMode);
  if (totalIncGst >= 1000 && !businessInfo?.address?.trim()) {
    issues.push({
      level: 'error',
      message: 'Orders of $1,000 or more require your business address on the document.',
    });
  }
  if (po.items.length === 0) {
    issues.push({ level: 'error', message: 'Purchase order has no line items.' });
  }
  return issues;
}

export function validateSupplierInvoiceRecord(
  invoice: Pick<SupplierInvoice, 'items' | 'gstMode' | 'invoiceNumber' | 'invoiceDate'>,
  supplier: Supplier | undefined,
  businessInfo?: BusinessInfo | null,
): InvoiceValidationIssue[] {
  const issues: InvoiceValidationIssue[] = [];
  if (!invoice.invoiceNumber?.trim()) {
    issues.push({ level: 'error', message: 'Supplier invoice number is required.' });
  }
  if (!invoice.invoiceDate) {
    issues.push({ level: 'error', message: 'Supplier invoice date is required.' });
  }
  if (!businessInfo?.businessName?.trim() || !businessInfo?.address?.trim()) {
    issues.push({
      level: 'warning',
      message: 'Complete your business name and address in Settings for a valid tax invoice record.',
    });
  }
  const gstMode = invoice.gstMode ?? 'inclusive';
  const { totalIncGst } = computePoTotals(linesToPoShape(invoice.items), gstMode);
  if (totalIncGst >= 1000 && !businessInfo?.address?.trim()) {
    issues.push({
      level: 'error',
      message: 'Tax invoices of $1,000 or more require your business address.',
    });
  }
  if (totalIncGst >= 1000 && !supplier?.gstNumber?.trim()) {
    issues.push({
      level: 'warning',
      message: 'Supplier GST number is recommended for invoices over $1,000.',
    });
  }
  if (invoice.items.length === 0) {
    issues.push({ level: 'error', message: 'Invoice must have at least one line.' });
  }
  return issues;
}

export function validateClientInvoiceRecord(
  invoice: Pick<ClientInvoice, 'items' | 'gstMode' | 'invoiceNumber' | 'invoiceDate'>,
  client: BillingClient | undefined,
  businessInfo?: BusinessInfo | null,
): InvoiceValidationIssue[] {
  const issues: InvoiceValidationIssue[] = [];
  if (!invoice.invoiceNumber?.trim()) {
    issues.push({ level: 'error', message: 'Invoice number is required.' });
  }
  if (!invoice.invoiceDate) {
    issues.push({ level: 'error', message: 'Invoice date is required.' });
  }
  if (!businessInfo?.businessName?.trim()) {
    issues.push({ level: 'error', message: 'Business name is required in Settings.' });
  }
  if (!businessInfo?.gstNumber?.trim()) {
    issues.push({ level: 'warning', message: 'Your GST number should be on outgoing tax invoices.' });
  }
  const gstMode = invoice.gstMode ?? 'inclusive';
  const { totalIncGst } = computePoTotals(linesToPoShape(invoice.items), gstMode);
  if (totalIncGst >= 1000 && !client?.address?.trim()) {
    issues.push({
      level: 'warning',
      message: 'Client address is recommended for invoices over $1,000.',
    });
  }
  if (invoice.items.length === 0) {
    issues.push({ level: 'error', message: 'Invoice must have at least one line.' });
  }
  return issues;
}

export function hasBlockingIssues(issues: InvoiceValidationIssue[]): boolean {
  return issues.some((i) => i.level === 'error');
}
